import { Box, Text } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import ChartEmptyState from '@/components/ChartEmptyState';
import ChartHeader from '@/components/ChartHeader';
import TechStackBadge from '@/components/TechStackBadge';
import { getEndpointWithParams } from '@/routes/config';
import { Candidate } from '@/services/candidates';
import { trioUITheme } from '@/styles/theme';
import { Paths } from '@/types/routes';
import * as Styles from './NewCandidatesTable.styles';
import Loading from '../general/Loading';

export type NewCandidatesTableProps = {
  title: string;
  tooltipMessage: string;
  onSelectFilter: (value: string) => void;
  periodOptions: any[];
  filteredCandidates: Candidate[];
  isLoading: boolean;
};

const NewCandidatesTable = ({
  title,
  tooltipMessage,
  onSelectFilter,
  periodOptions,
  filteredCandidates,
  isLoading,
}: NewCandidatesTableProps): JSX.Element => (
  <Box
    data-testid="new-candidates-table"
    display="flex"
    flexDirection="column"
    shadow="0px 4px 6px rgba(0, 0, 0, 0.07)"
    py="38px"
    pl="32px"
    mb="38px"
    pr="18px"
    bg="white"
    borderRadius="16px"
    height="573px"
    maxW="100%"
  >
    <Box pr="14px">
      <ChartHeader
        title={title}
        tooltipMessage={tooltipMessage}
        onSelectFilter={onSelectFilter}
        filterOptions={periodOptions}
      />
    </Box>

    {filteredCandidates && filteredCandidates.length > 0 ? (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="center"
        rowGap="8px"
        overflowY="auto"
        mt="16px"
        pr="8px"
        sx={{
          '&::-webkit-scrollbar': {
            width: '6px',
            backgroundColor: 'white',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: trioUITheme.colors['neutral-40'],
            borderRadius: '4px',
          },
        }}
      >
        {filteredCandidates.map((candidate: any) => (
          <Box
            key={`${candidate.candidateOwner}-${candidate.id}`}
            bg={trioUITheme.colors['neutral-20']}
            border={`1px solid ${trioUITheme.colors['neutral-30']}`}
            borderRadius="8px"
            py="8px"
            px="8px"
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            w="100%"
          >
            <Styles.CustomUserInitials
              firstName={candidate.firstName}
              lastName={candidate?.lastName ?? ''}
            />
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="flex-start"
            >
              <Box display="flex" justifyContent="center" alignItems="center">
                <Link to={getEndpointWithParams(Paths.CANDIDATE_DETAILS, { id: candidate.id })}>
                  <Text fontWeight={600} fontSize="16px" color={trioUITheme.colors['primary-60']}>
                    {`${candidate.firstName} ${candidate.lastName}`}
                  </Text>
                </Link>
                <Text
                  mx="12px"
                  fontWeight={400}
                  fontSize="14px"
                  color={trioUITheme.colors['neutral-90']}
                >
                  |
                </Text>
                <Text fontWeight={400} fontSize="14px" color={trioUITheme.colors['neutral-90']}>
                  {candidate.status}
                </Text>
                <Text
                  mx="12px"
                  fontWeight={400}
                  fontSize="14px"
                  color={trioUITheme.colors['neutral-90']}
                >
                  |
                </Text>
                <Text fontWeight={400} fontSize="14px" color={trioUITheme.colors['neutral-90']}>
                  {candidate.candidateOwner}&apos;s
                </Text>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="center"
                flexWrap="wrap"
                gap="6px"
                mt="5px"
              >
                <Styles.StatusBadge status={candidate.status} outlined />
                {candidate.mainTechStack &&
                  candidate.mainTechStack.map((tag: string) => (
                    <TechStackBadge key={tag} title={tag} />
                  ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    ) : (
      <Box height="100%" display="flex" justifyContent="center" alignItems="center">
        <ChartEmptyState
          title="Your search didn’t match any result"
          subTitle="Try to choose different options."
        />
      </Box>
    )}
    {isLoading && (
      <Box height="100%" display="flex" justifyContent="center" alignItems="center">
        <Loading size={25} />
      </Box>
    )}
  </Box>
);

export default NewCandidatesTable;
